import { Box, IconButton, Skeleton, Typography } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import React from 'react'
import { useTranslation } from 'react-i18next'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useRatesDataGridContext } from '../contexts/ratesDataGrid/RatesDataGridContext'
import {
  CapacityRow,
  ChannelRow,
  RateRow,
  RatesDataGridRow,
  RowType
} from '../contexts/ratesDataGrid/RatesDataGridContext.types'

interface Props {
  cellParams: GridRenderCellParams
}

const RatesGridCol1CellRender: ReactFC<Props> = ({ cellParams }) => {
  const { row: renderRow } = cellParams
  const row = renderRow as RatesDataGridRow

  const {
    openRateCapacityRows,
    closeRateCapacityRows,
    openRateChannelRows,
    closeRateChannelRows,
    channelLoaderControllers,
    capacityLoaderControllers
  } = useRatesDataGridContext()

  const { t } = useTranslation()

  if (
    row.type === RowType.CAPACITY_SKELETON ||
    row.type === RowType.CHANNEL_SKELETON
  ) {
    return <Skeleton variant='rectangular' width='100%' height='60%' />
  }

  if (row.type === RowType.HOTEL) {
    return <Typography>{row.data.name}</Typography>
  }

  if (row.type === RowType.ROOM_TYPE) {
    return (
      <Typography>{`${row.data.name} (${row.data.totalRooms})`}</Typography>
    )
  }

  const rateRow = row as RateRow | CapacityRow | ChannelRow

  const haveCapacities = 'capacitiesQty' in rateRow && rateRow.capacitiesQty > 0

  const haveChannels = 'channelsQty' in rateRow && rateRow.channelsQty > 0

  const channelsLoading = channelLoaderControllers.current[row.id] !== undefined

  const capacitiesLoading =
    capacityLoaderControllers.current[row.id] !== undefined

  const handleToggleCapacities = async () => {
    const toggledRow = rateRow as RateRow
    if (toggledRow.showCapacities) {
      return closeRateCapacityRows({
        row: toggledRow
      })
    }
    openRateCapacityRows({
      row: toggledRow
    })
  }

  const handleToggleChannels = async () => {
    const toggledRow = rateRow as RateRow | CapacityRow
    if (toggledRow.showChannels) {
      return closeRateChannelRows({
        row: toggledRow
      })
    }
    openRateChannelRows({
      row: toggledRow
    })
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <>
        {haveCapacities && (
          <IconButton
            onClick={(e) => {
              e.preventDefault()
              handleToggleCapacities()
            }}
            sx={{
              width: '1rem',
              height: '1rem',
              marginRight: '0.5rem'
            }}
            disabled={channelsLoading}
          >
            {rateRow.showCapacities ? (
              <ArrowDropDownIcon />
            ) : (
              <ArrowDropDownIcon
                sx={{
                  transform: 'rotate(-90deg)'
                }}
              />
            )}
          </IconButton>
        )}
        <Box
          sx={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            width: 'calc(100% - 1.5rem)'
          }}
        >
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              wordWrap: 'break-word',
              whiteSpace: 'nowrap',
              ...(row.type === RowType.CHANNEL && {
                marginLeft: 'auto',
                color: 'primary.main'
              }),
              fontWeight: '500',
              paddingRight: '.1rem'
            }}
          >
            {rateRow.data.name}
          </Typography>
          {haveChannels && (
            <Box
              onClick={(e) => {
                e.preventDefault()
                if (!capacitiesLoading) handleToggleChannels()
              }}
              sx={{
                marginLeft: 'auto',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: capacitiesLoading ? 'gray' : undefined
              }}
            >
              <Typography
                sx={{
                  fontSize: '.9em'
                }}
              >
                {/* {renderCellRow.type !== 'channel' && channels}{' '} */}
                {`${rateRow.channelsQty} ${t(
                  'apps.rates.rateDataGrid.labels.channels'
                )}`}
              </Typography>
              <ArrowDropDownIcon
                sx={{
                  transform: rateRow.showChannels ? 'none' : 'rotate(-90deg)',
                  fontSize: '1em'
                }}
              />
            </Box>
          )}
        </Box>
      </>
    </Box>
  )
}
export default React.memo(RatesGridCol1CellRender)
